import { useCallback, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { ALLOWED_RECEIPT_TYPES } from '@gdec/shared';
import { Spinner } from '@/components/Spinner';

export type QueueStatus = 'working' | 'ok' | 'warn' | 'error';

export interface QueueItem {
  id: string;
  name: string;
  status: QueueStatus;
  message: string;
}

const ACCEPT = ALLOWED_RECEIPT_TYPES.join(',');

export interface ReceiptDropzoneProps {
  onFiles: (files: readonly File[]) => void;
  disabled?: boolean;
}

/**
 * Drag-and-drop plus a real file input.
 *
 * The input is visible rather than hidden behind a click-forwarding div: it is
 * the only element here that a keyboard or screen reader can operate, so hiding
 * it would make uploading a mouse-only feature.
 */
export function ReceiptDropzone({ onFiles, disabled = false }: ReceiptDropzoneProps): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragover, setDragover] = useState(false);

  const emit = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) return;
      onFiles(Array.from(list));
    },
    [onFiles],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragover(false);
      if (disabled) return;
      emit(e.dataTransfer.files);
    },
    [disabled, emit],
  );

  return (
    <div
      className={dragover ? 'cf-dropzone dragover' : 'cf-dropzone'}
      onDragEnter={(e) => {
        e.preventDefault();
        if (!disabled) setDragover(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragover(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragover(false);
      }}
      onDrop={onDrop}
      onClick={(e) => {
        // Clicking anywhere in the zone opens the picker, except on the input
        // itself, which would otherwise open it twice.
        if (disabled) return;
        if (e.target === inputRef.current) return;
        inputRef.current?.click();
      }}
    >
      <div className="icon" aria-hidden="true">
        🧾
      </div>
      <div className="title">Drop receipt photos or PDFs here, or choose files</div>
      <div className="desc">
        Each file is read automatically — date, amount, vendor, TIN, and VAT status get filled in
        for you. Review before submitting.
      </div>
      <input
        ref={inputRef}
        className="cf-file-input"
        type="file"
        accept={ACCEPT}
        multiple
        disabled={disabled}
        aria-label="Choose receipt files to upload"
        onChange={(e) => {
          emit(e.target.files);
          // Reset so re-picking the same file still fires a change event.
          e.target.value = '';
        }}
      />
    </div>
  );
}

const ICON: Record<QueueStatus, string> = {
  working: '',
  ok: '✓',
  warn: '⚠',
  error: '⚠',
};

export function UploadQueue({
  items,
  onDismiss,
}: {
  items: readonly QueueItem[];
  onDismiss: (id: string) => void;
}): ReactNode {
  if (items.length === 0) return null;
  return (
    <div className="cf-queue" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className={`cf-queue-item ${item.status}`}>
          <span className="ic" aria-hidden="true">
            {item.status === 'working' ? <Spinner /> : ICON[item.status]}
          </span>
          <div className="msg">
            <b>{item.name}</b> — {item.message}
          </div>
          {item.status === 'working' ? null : (
            <button
              type="button"
              className="cf-queue-dismiss"
              onClick={() => onDismiss(item.id)}
              aria-label={`Dismiss status for ${item.name}`}
            >
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
